// Personalization Engine — maps quiz goals to recommended add-on categories

export type PersonalizationTag = "METABOLIC" | "ENERGY" | "RECOVERY" | "HORMONAL";

export interface AddOn {
  id: PersonalizationTag;
  name: string;
  shortBenefit: string;
  description: string;
  priceId: string;
  priceCents: number;
  color: string; // tailwind bg class token
}

// Stripe price IDs from created products
export const CORE_PRICE_ID = "price_1U2OsfAjtpy6mbnbTKfRePuc";
export const CORE_PRICE_CENTS = 34900;

export const ADD_ONS: AddOn[] = [
  {
    id: "METABOLIC",
    name: "Metabolic Support",
    shortBenefit: "May support weight management & body composition",
    description:
      "Commonly selected by patients whose goals include sustainable weight management. Your provider will determine eligibility based on your clinical profile.",
    priceId: "price_1TLyby1jBwrJ80SXulPPQgTa",
    priceCents: 15000,
    color: "bg-primary/10 border-primary/30",
  },
  {
    id: "ENERGY",
    name: "Energy & Cellular Support",
    shortBenefit: "May support cellular energy & mental clarity",
    description:
      "Patients with similar profiles often explore options designed to support mitochondrial function, sustained energy, and cognitive performance.",
    priceId: "price_1TLycK1jBwrJ80SX5oJj4l9w",
    priceCents: 11500,
    color: "bg-blue-500/10 border-blue-500/30",
  },
  {
    id: "RECOVERY",
    name: "Recovery & Performance",
    shortBenefit: "May support tissue repair & physical performance",
    description:
      "Designed for patients seeking support with joint health, physical recovery, and performance optimization. Provider determines treatment eligibility.",
    priceId: "price_1TLyw51jBwrJ80SXdxphRO5J",
    priceCents: 11500,
    color: "bg-purple-500/10 border-purple-500/30",
  },
  {
    id: "HORMONAL",
    name: "Hormonal Optimization",
    shortBenefit: "May support hormonal balance & vitality",
    description:
      "Commonly explored by patients looking to support hormonal balance, libido, and long-term vitality. All treatments require provider approval.",
    priceId: "price_1TLywO1jBwrJ80SX9ntkgeOB",
    priceCents: 17500,
    color: "bg-orange-500/10 border-orange-500/30",
  },
];

// Priority order for auto-selecting max 2
const PRIORITY: PersonalizationTag[] = ["METABOLIC", "ENERGY", "RECOVERY", "HORMONAL"];

/**
 * Map quiz goal strings → personalization tags
 */
export function mapGoalsToTags(goals: string[]): PersonalizationTag[] {
  const tags = new Set<PersonalizationTag>();

  for (const goal of goals) {
    const g = goal.toLowerCase();
    if (g.includes("weight") || g.includes("metabol")) tags.add("METABOLIC");
    if (g.includes("energy") || g.includes("clarity") || g.includes("mental")) tags.add("ENERGY");
    if (g.includes("recovery") || g.includes("performance")) tags.add("RECOVERY");
    if (g.includes("hormone") || g.includes("long-term") || g.includes("longevity") || g.includes("optimize")) tags.add("HORMONAL");
  }

  return Array.from(tags);
}

/**
 * Given tags, return recommended add-ons with at most 2 pre-selected.
 * Returns all add-ons with a `recommended` flag.
 */
export function getRecommendedAddons(
  tags: PersonalizationTag[]
): (AddOn & { recommended: boolean })[] {
  const tagSet = new Set(tags);

  // Pick top 2 by priority among matched tags
  const preSelected = PRIORITY.filter((t) => tagSet.has(t)).slice(0, 2);
  const preSelectedSet = new Set(preSelected);

  return ADD_ONS.map((addon) => ({
    ...addon,
    recommended: preSelectedSet.has(addon.id),
  }));
}

/** Pre-built stack shortcuts */
export const STACKS = [
  {
    name: "Metabolic Foundation",
    description: "Starting at $349–$449/month",
    addons: ["METABOLIC", "ENERGY"] as PersonalizationTag[],
  },
  {
    name: "Energy & Performance",
    description: "Starting at $429–$499/month",
    addons: ["ENERGY", "RECOVERY"] as PersonalizationTag[],
  },
  {
    name: "Longevity Optimization",
    description: "Starting at $499–$649/month",
    addons: ["HORMONAL", "RECOVERY"] as PersonalizationTag[],
  },
];

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}
